import { useMemo } from "react";
import { isInteractiveQuiz } from "../../utils/pollTypes.js";
import { updatePollItem, removePollItem, patchPollLocal, clearPollChannel } from "../../hooks/adminStore.js";

const realPollsOnly = (polls) => (Array.isArray(polls) ? polls : []).filter((p) => p && typeof p === "object" && !isInteractiveQuiz(p));

function matchId(poll, targetId) {
  return String(poll.id) === String(targetId);
}

export default function usePollControls(session, showMsg) {
  const polls = useMemo(() => realPollsOnly(session?.polls), [session?.polls]);

  function pollToggle(id, isApproved) {
    if (!session) {
      console.warn("[pollToggle] no session", id, isApproved);
      return;
    }
    const all = session.polls || polls;
    const poll = all.find((p) => matchId(p, id)) || polls.find((p) => matchId(p, id));
    if (!poll) {
      console.warn("[pollToggle] poll not found", id);
      showMsg?.(`Poll ${id} not found`, "error");
      return;
    }
    console.log(`[pollToggle] ${id} -> isApproved=${isApproved} id=${poll.id}`);
    if (isApproved) {
      updatePollItem(session.sessionId, { ...poll, isApproved: true, showresult: false });
    } else {
      removePollItem(session.sessionId, String(poll.id));
    }
  }

  function pollType(id, type) {
    if (!session) {
      console.warn("[pollType] no session", id, type);
      return;
    }
    const all = session.polls || polls;
    const poll = all.find((p) => matchId(p, id)) || polls.find((p) => matchId(p, id));
    if (!poll) {
      console.warn("[pollType] poll not found", id);
      return;
    }
    // A session start / end poll always stays a popup.
    const isStagePoll = poll.sessionStartPoll === true || poll.sessionEndPoll === true;
    const pollTypeVal = type === "popup" || isStagePoll ? "popup" : "section";
    console.log(`[pollType] ${id} -> ${pollTypeVal} (was ${poll.pollType}) isApproved=${poll.isApproved}`);
    patchPollLocal(session.sessionId, id, { pollType: pollTypeVal });
    if (poll.isApproved) {
      updatePollItem(session.sessionId, { ...poll, pollType: pollTypeVal, isApproved: true, showresult: poll.showresult === true });
    }
  }

  // "Start / End poll": stage "start" = sessionStartPoll, "end" = sessionEndPoll, null = off.
  // Only one at a time. Turning one on sets pollType popup and publishes the poll as
  // active with no results shown; turning it off deactivates the poll.
  function pollStage(id, stage) {
    if (!session) {
      console.warn("[pollStage] no session", id, stage);
      return;
    }
    const all = session.polls || polls;
    const poll = all.find((p) => matchId(p, id)) || polls.find((p) => matchId(p, id));
    if (!poll) {
      console.warn("[pollStage] poll not found", id);
      return;
    }
    const patch = {
      sessionStartPoll: stage === "start",
      sessionEndPoll: stage === "end",
      ...(stage ? { pollType: "popup", showresult: false } : {}),
    };
    console.log(`[pollStage] ${id} -> ${stage || "off"} isApproved=${poll.isApproved}`);
    patchPollLocal(session.sessionId, id, patch);
    if (stage) {
      updatePollItem(session.sessionId, { ...poll, ...patch, isApproved: true });
    } else {
      removePollItem(session.sessionId, String(poll.id));
    }
  }

  function pollShowResult(id, showresult) {
    if (!session) {
      console.warn("[pollShowResult] no session", id, showresult);
      return;
    }
    const all = session.polls || polls;
    const poll = all.find((p) => matchId(p, id)) || polls.find((p) => matchId(p, id));
    if (!poll) {
      console.warn("[pollShowResult] poll not found", id);
      showMsg?.(`Poll ${id} not found`, "error");
      return;
    }
    // A session start / end poll never shows results.
    if (showresult && (poll.sessionStartPoll === true || poll.sessionEndPoll === true)) return;
    console.log(`[pollShowResult] ${id} -> showresult=${showresult} id=${poll.id}`);
    updatePollItem(session.sessionId, { ...poll, isApproved: true, showresult: showresult === true });
  }

  function clearPollChannelData() {
    if (!session) {
      console.warn("[clearPollChannel] no session");
      return;
    }
    console.log(`[clearPollChannel] clearing poll/survey for session ${session.sessionId}`);
    clearPollChannel(session.sessionId);
  }

  return { polls, pollToggle, pollType, pollStage, pollShowResult, clearPollChannel: clearPollChannelData };
}
